"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { HiChevronDown, HiChevronUp, HiOutlinePencilAlt } from "react-icons/hi";
import dynamic from "next/dynamic";
import { EmailComposerSkeleton } from "@/components/email-composer/email-skeleton";
import type { ComposerDraftFlush } from "@/components/email-composer/hooks/use-email-draft-autosave";
import { Badge, Button } from "@/components/ui";
import { formatTime } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { EmailMessage, EmailParticipant, EmailThreadScope, getFromParticipant } from "@/types/email";
import { EmailParticipantRole } from "@/types/enums/email";
import { EmailActionButtons } from "./email-action-buttons";
import { EmailAttachments } from "./email-attachments";
import { EmailBodyRenderer } from "./email-body-renderer";
import type { ResumeDraftInfo } from "./email-reply-composer";
import { EmailThemeToggle, useEmailThemeToggle } from "./email-theme-toggle";
import { useMessageAttachmentUrls } from "@/api/provider/comms/email/email-query";

// The tiptap/react-email composer chain is heavy and only needed once a
// message row is actually replied to or forwarded — load it on demand instead
// of shipping it with every message in the thread.
const EmailForwardComposer = dynamic(() => import("./email-forward-composer").then(m => m.EmailForwardComposer), {
  loading: () => <EmailComposerSkeleton />
});
const EmailReplyComposer = dynamic(() => import("./email-reply-composer").then(m => m.EmailReplyComposer), {
  loading: () => <EmailComposerSkeleton />
});
interface EmailMessageItemProps {
  message: EmailMessage;
  defaultExpanded?: boolean;
  threadId?: string;
  chatId?: string;
  threadScope?: EmailThreadScope;
  showActions?: boolean;
  replyingToMessageId?: string | null;
  onReply?: (messageId: string | null) => void;
  onReplySent?: () => void;
  forwardingMessageId?: string | null;
  onForward?: (messageId: string | null) => void;
  autoScrollOnExpand?: boolean;
  /** Passed to an open composer so the thread view can await its draft flush. */
  registerComposerFlush?: (flush: ComposerDraftFlush | null) => void;
  /** The thread's in-progress draft, resumed when this message's composer opens. */
  resumeDraft?: ResumeDraftInfo;
}

// function formatTimestamp(dateString: string | null): string {
//   if (!dateString) return "";

//   const date = new Date(dateString);
//   const now = new Date();
//   const diffMs = now.getTime() - date.getTime();
//   const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

//   // Time format
//   const timeOptions: Intl.DateTimeFormatOptions = {
//     hour: "numeric",
//     minute: "2-digit",
//     hour12: true,
//   };
//   const time = date.toLocaleTimeString(undefined, timeOptions);

//   // Same day - show just time
//   if (diffDays === 0 && date.toDateString() === now.toDateString()) {
//     return time;
//   }

//   // Within last week - show weekday and time
//   if (diffDays < 7) {
//     const weekday = date.toLocaleDateString(undefined, { weekday: "short" });
//     return `${weekday}, ${time}`;
//   }

//   // Same year - show month and day
//   if (date.getFullYear() === now.getFullYear()) {
//     const dateStr = date.toLocaleDateString(undefined, {
//       month: "short",
//       day: "numeric",
//     });
//     return `${dateStr}, ${time}`;
//   }

//   // Different year - show full date
//   const dateStr = date.toLocaleDateString(undefined, {
//     year: "numeric",
//     month: "short",
//     day: "numeric",
//   });
//   return `${dateStr}, ${time}`;
// }

/**
 * Cleans a collapsed-row preview snippet: drops everything from the first
 * quote header ("On … wrote:") onward and strips leftover ">" quote markers,
 * so the preview shows only the new message text instead of nested quote noise.
 */
function cleanSnippet(raw: string | null | undefined): string {
  if (!raw) return "No preview available";
  // Match Gmail/Apple-Mail quote headers like "On <weekday>, <Mon> <day> … wrote:".
  // The weekday is optional (many clients omit it), so we require a month-like
  // token + day number instead, and bound the gap (~80 chars) so the split can't
  // swallow unrelated content when "wrote:" appears later in the message.
  const beforeQuote = raw.split(/\bON\s+(?:(?:MON|TUE|WED|THU|FRI|SAT|SUN),?\s+)?[A-Z]{3,}\s+\d{1,2}[\s\S]{0,80}?\bWROTE:/i)[0];
  const cleaned = beforeQuote.replace(/(^|\s)>+/g, " ").replace(/\s+/g, " ").trim();
  return cleaned || "No preview available";
}
function formatParticipantName(participant: EmailParticipant | null): string {
  if (!participant) return "Unknown";
  const contact = participant.contact;
  return contact.displayName || contact.email || "Unknown";
}
function formatParticipantEmail(participant: EmailParticipant | null): string {
  if (!participant) return "";
  return participant.contact.email || "";
}
interface ParticipantListProps {
  participants: EmailParticipant[];
  role: "TO" | "CC" | "BCC";
  label: string;
}
function ParticipantList(t0) {
  const $ = _c(9);
  const {
    participants,
    role,
    label
  } = t0;
  let t1;
  let t2;
  if ($[0] !== label || $[1] !== participants || $[2] !== role) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t3;
      if ($[5] !== role) {
        t3 = p => p.role === role;
        $[5] = role;
        $[6] = t3;
      } else {
        t3 = $[6];
      }
      const filtered = participants.filter(t3);
      if (filtered.length === 0) {
        t2 = null;
        break bb0;
      }
      const displayNames = filtered.map(_temp).filter(_temp2);
      let t4;
      if ($[7] !== label) {
        t4 = <span className="text-foreground-muted">{label}:</span>;
        $[7] = label;
        $[8] = t4;
      } else {
        t4 = $[8];
      }
      t1 = <div className="text-caption text-foreground-secondary truncate">{t4}{" "}{displayNames.map(_temp3).join(", ")}</div>;
    }
    $[0] = label;
    $[1] = participants;
    $[2] = role;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  return t1;
}
function _temp3(p_2) {
  return p_2.displayName ? p_2.displayName + ` <${p_2.email}>` : p_2.email;
}
function _temp2(p_1) {
  return p_1.displayName || p_1.email;
}
function _temp(p_0) {
  return {
    displayName: p_0.contact.displayName,
    email: p_0.contact.email
  };
}
export function EmailMessageItem(t0) {
  const $ = _c(130);
  const {
    message,
    defaultExpanded,
    threadId,
    chatId,
    threadScope: t1,
    showActions: t2,
    replyingToMessageId,
    onReply,
    onReplySent,
    forwardingMessageId,
    onForward,
    autoScrollOnExpand: t3,
    registerComposerFlush,
    resumeDraft
  } = t0;
  const threadScope = t1 === undefined ? "provider" : t1;
  const showActions = t2 === undefined ? true : t2;
  const autoScrollOnExpand = t3 === undefined ? true : t3;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? false);
  const {
    override,
    mode,
    isOverridden,
    toggle
  } = useEmailThemeToggle();
  const itemRef = useRef(null);
  const replyRef = useRef(null);
  let t4;
  let t5;
  if ($[0] !== autoScrollOnExpand || $[1] !== defaultExpanded) {
    t4 = () => {
      if (autoScrollOnExpand && defaultExpanded && itemRef.current) {
        requestAnimationFrame(() => {
          if (typeof itemRef.current?.scrollIntoView === "function") {
            itemRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
          }
        });
      }
    };
    t5 = [autoScrollOnExpand, defaultExpanded];
    $[0] = autoScrollOnExpand;
    $[1] = defaultExpanded;
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  const isReplying = replyingToMessageId === message.id;
  const isForwarding = forwardingMessageId === message.id;
  const forwardRef = useRef(null);
  let t6;
  let t7;
  if ($[4] !== isReplying) {
    t6 = () => {
      if (isReplying && replyRef.current) {
        requestAnimationFrame(() => {
          if (typeof replyRef.current?.scrollIntoView === "function") {
            replyRef.current.scrollIntoView({
              behavior: "smooth",
              block: "end"
            });
          }
        });
      }
    };
    t7 = [isReplying];
    $[4] = isReplying;
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[7] !== isForwarding) {
    t8 = () => {
      if (isForwarding && forwardRef.current) {
        requestAnimationFrame(() => {
          if (typeof forwardRef.current?.scrollIntoView === "function") {
            forwardRef.current.scrollIntoView({
              behavior: "smooth",
              block: "end"
            });
          }
        });
      }
    };
    t9 = [isForwarding];
    $[7] = isForwarding;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  useEffect(t8, t9);
  let t10;
  if ($[10] !== message.attachments) {
    t10 = message.attachments.some(_temp4);
    $[10] = message.attachments;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const hasInlineAttachments = t10;
  const t11 = isExpanded && message.attachments.length > 0;
  let t12;
  if ($[12] !== t11) {
    t12 = {
      enabled: t11
    };
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const {
    data: attachmentUrls,
    isLoading: isLoadingAttachmentUrls
  } = useMessageAttachmentUrls(message.id, t12);
  const isLoadingInlineImages = hasInlineAttachments && isLoadingAttachmentUrls;
  let t13;
  if ($[14] !== message.participants) {
    t13 = getFromParticipant(message.participants);
    $[14] = message.participants;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const fromParticipant = t13;
  const t14 = message.sentDate || message.createdDate;
  let t15;
  if ($[16] !== t14) {
    t15 = formatTime(t14);
    $[16] = t14;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  const timestamp = t15;
  let t16;
  if ($[18] !== fromParticipant) {
    t16 = formatParticipantName(fromParticipant ? {
      id: "",
      role: EmailParticipantRole.FROM,
      contact: fromParticipant
    } : null);
    $[18] = fromParticipant;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const fromName = t16;
  let t17;
  if ($[20] !== fromParticipant) {
    t17 = formatParticipantEmail(fromParticipant ? {
      id: "",
      role: EmailParticipantRole.FROM,
      contact: fromParticipant
    } : null);
    $[20] = fromParticipant;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  const fromEmail = t17;
  if (message.isDraft) {
    const isForwardDraft = message.draftKind === "FORWARD";
    let t18;
    if ($[22] !== chatId || $[23] !== isForwardDraft || $[24] !== isForwarding || $[25] !== isReplying || $[26] !== message || $[27] !== onForward || $[28] !== onReply || $[29] !== onReplySent || $[30] !== registerComposerFlush || $[31] !== resumeDraft || $[32] !== showActions || $[33] !== threadId || $[34] !== threadScope) {
      t18 = <div ref={itemRef} className="overflow-hidden">{(isReplying || isForwarding) && threadId ? isForwarding ? <div ref={forwardRef} className="px-4 py-4"><EmailForwardComposer threadId={threadId} message={message} threadScope={threadScope} resumeDraft={resumeDraft} registerComposerFlush={registerComposerFlush} onSent={() => onForward?.(null)} onCancel={() => onForward?.(null)} /></div> : <div ref={replyRef} className="px-4 py-4"><EmailReplyComposer threadId={threadId} chatId={chatId} threadScope={threadScope} message={message} resumeDraft={resumeDraft} registerComposerFlush={registerComposerFlush} onSent={() => {
            onReply?.(null);
            onReplySent?.();
          }} onCancel={() => onReply?.(null)} /></div> : <div className="flex items-center gap-3 px-4 py-3"><Badge variant="default" size="xs">Draft</Badge><span className="min-w-0 flex-1 truncate text-caption text-foreground-secondary">{cleanSnippet(message.snippet)}</span>{showActions && <Button variant="ghost" size="sm" icon={<HiOutlinePencilAlt className="h-3.5 w-3.5" />} onClick={() => isForwardDraft ? onForward?.(message.id) : onReply?.(message.id)}>Edit draft</Button>}</div>}</div>;
      $[22] = chatId;
      $[23] = isForwardDraft;
      $[24] = isForwarding;
      $[25] = isReplying;
      $[26] = message;
      $[27] = onForward;
      $[28] = onReply;
      $[29] = onReplySent;
      $[30] = registerComposerFlush;
      $[31] = resumeDraft;
      $[32] = showActions;
      $[33] = threadId;
      $[34] = threadScope;
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    return t18;
  }
  const t18 = !isExpanded && "opacity-70 hover:bg-row-hover/60";
  let t19;
  if ($[36] !== t18) {
    t19 = cx("relative overflow-hidden transition-colors", t18);
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const t20 = !isExpanded && "hover:bg-hover";
  let t21;
  if ($[38] !== t20) {
    t21 = cx("flex items-start gap-3 px-4 py-3 transition-colors", t20);
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== isExpanded) {
    t22 = () => setIsExpanded(!isExpanded);
    $[40] = isExpanded;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== fromName) {
    t23 = <span className="truncate text-body-emphasis text-foreground">{fromName}</span>;
    $[42] = fromName;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== fromEmail || $[45] !== fromName) {
    t24 = fromEmail && fromName !== fromEmail && <span className="hidden truncate text-caption text-foreground-muted sm:inline">{"<"}{fromEmail}{">"}</span>;
    $[44] = fromEmail;
    $[45] = fromName;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t23 || $[48] !== t24) {
    t25 = <div className="flex min-w-0 items-center gap-2">{t23}{t24}</div>;
    $[47] = t23;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== isExpanded || $[51] !== message.snippet) {
    t26 = !isExpanded && <div className="mt-0.5 truncate text-caption text-foreground-secondary">{cleanSnippet(message.snippet)}</div>;
    $[50] = isExpanded;
    $[51] = message.snippet;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== isExpanded || $[54] !== message.participants) {
    t27 = isExpanded && <div className="mt-2 space-y-0.5"><ParticipantList participants={message.participants} role="TO" label="To" /><ParticipantList participants={message.participants} role="CC" label="Cc" /><ParticipantList participants={message.participants} role="BCC" label="Bcc" /></div>;
    $[53] = isExpanded;
    $[54] = message.participants;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t25 || $[57] !== t26 || $[58] !== t27) {
    t28 = <div className="min-w-0">{t25}{t26}{t27}</div>;
    $[56] = t25;
    $[57] = t26;
    $[58] = t27;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== isExpanded || $[61] !== t22 || $[62] !== t28) {
    t29 = <button type="button" onClick={t22} className="min-w-0 flex-1 cursor-pointer text-left outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-expanded={isExpanded}>{t28}</button>;
    $[60] = isExpanded;
    $[61] = t22;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== isExpanded || $[65] !== isOverridden || $[66] !== message.bodyHtml || $[67] !== mode || $[68] !== toggle) {
    t30 = isExpanded && message.bodyHtml && <EmailThemeToggle mode={mode} onToggle={toggle} className={cx("text-foreground-muted", isOverridden && "text-primary")} />;
    $[64] = isExpanded;
    $[65] = isOverridden;
    $[66] = message.bodyHtml;
    $[67] = mode;
    $[68] = toggle;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== isExpanded || $[71] !== message || $[72] !== onForward || $[73] !== onReply || $[74] !== showActions) {
    t31 = isExpanded && showActions && <EmailActionButtons message={message} onReply={messageId => onReply?.(messageId)} onForward={messageId_0 => onForward?.(messageId_0)} className="flex items-center gap-1" />;
    $[70] = isExpanded;
    $[71] = message;
    $[72] = onForward;
    $[73] = onReply;
    $[74] = showActions;
    $[75] = t31;
  } else {
    t31 = $[75];
  }
  let t32;
  if ($[76] !== timestamp) {
    t32 = <span className="py-1 text-caption text-foreground-muted">{timestamp}</span>;
    $[76] = timestamp;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  let t33;
  if ($[78] !== isExpanded) {
    t33 = () => setIsExpanded(!isExpanded);
    $[78] = isExpanded;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  const t34 = isExpanded ? "Collapse email" : "Expand email";
  let t35;
  if ($[80] !== isExpanded) {
    t35 = isExpanded ? <HiChevronUp className="h-4 w-4" /> : <HiChevronDown className="h-4 w-4" />;
    $[80] = isExpanded;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] !== isExpanded || $[83] !== t33 || $[84] !== t34 || $[85] !== t35) {
    t36 = <button type="button" onClick={t33} className="rounded p-1 text-foreground-muted transition-colors hover:bg-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={t34} aria-expanded={isExpanded}>{t35}</button>;
    $[82] = isExpanded;
    $[83] = t33;
    $[84] = t34;
    $[85] = t35;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] !== t30 || $[88] !== t31 || $[89] !== t32 || $[90] !== t36) {
    t37 = <div className="flex shrink-0 items-center gap-2">{t30}{t31}{t32}{t36}</div>;
    $[87] = t30;
    $[88] = t31;
    $[89] = t32;
    $[90] = t36;
    $[91] = t37;
  } else {
    t37 = $[91];
  }
  let t38;
  if ($[92] !== t21 || $[93] !== t29 || $[94] !== t37) {
    t38 = <div className={t21}>{t29}{t37}</div>;
    $[92] = t21;
    $[93] = t29;
    $[94] = t37;
    $[95] = t38;
  } else {
    t38 = $[95];
  }
  let t39;
  if ($[96] !== attachmentUrls?.byContentId || $[97] !== isExpanded || $[98] !== isLoadingInlineImages || $[99] !== message.attachments || $[100] !== message.bodyHtml || $[101] !== message.bodyText || $[102] !== message.direction || $[103] !== message.messageType || $[104] !== override) {
    t39 = isExpanded && <div className="px-4 pb-4 pt-1"><EmailBodyRenderer bodyHtml={message.bodyHtml} bodyText={message.bodyText} themeOverride={override} inlineImageUrls={attachmentUrls?.byContentId} isLoadingInlineImages={isLoadingInlineImages} direction={message.direction} messageType={message.messageType} />{message.attachments.length > 0 && <div className="pt-3"><EmailAttachments attachments={message.attachments} /></div>}</div>;
    $[96] = attachmentUrls?.byContentId;
    $[97] = isExpanded;
    $[98] = isLoadingInlineImages;
    $[99] = message.attachments;
    $[100] = message.bodyHtml;
    $[101] = message.bodyText;
    $[102] = message.direction;
    $[103] = message.messageType;
    $[104] = override;
    $[105] = t39;
  } else {
    t39 = $[105];
  }
  let t40;
  if ($[106] !== chatId || $[107] !== isReplying || $[108] !== message || $[109] !== onReply || $[110] !== onReplySent || $[111] !== registerComposerFlush || $[112] !== resumeDraft || $[113] !== threadId || $[114] !== threadScope) {
    t40 = isReplying && threadId && <div ref={replyRef} className="border-t border-border/60 px-4 py-4"><EmailReplyComposer threadId={threadId} chatId={chatId} threadScope={threadScope} message={message} resumeDraft={resumeDraft} registerComposerFlush={registerComposerFlush} onSent={() => {
        onReply?.(null);
        onReplySent?.();
      }} onCancel={() => onReply?.(null)} /></div>;
    $[106] = chatId;
    $[107] = isReplying;
    $[108] = message;
    $[109] = onReply;
    $[110] = onReplySent;
    $[111] = registerComposerFlush;
    $[112] = resumeDraft;
    $[113] = threadId;
    $[114] = threadScope;
    $[115] = t40;
  } else {
    t40 = $[115];
  }
  let t41;
  if ($[116] !== isForwarding || $[117] !== message || $[118] !== onForward || $[119] !== registerComposerFlush || $[120] !== resumeDraft || $[121] !== threadId || $[122] !== threadScope) {
    t41 = isForwarding && threadId && <div ref={forwardRef} className="border-t border-border/60 px-4 py-4"><EmailForwardComposer threadId={threadId} message={message} threadScope={threadScope} resumeDraft={resumeDraft} registerComposerFlush={registerComposerFlush} onSent={() => onForward?.(null)} onCancel={() => onForward?.(null)} /></div>;
    $[116] = isForwarding;
    $[117] = message;
    $[118] = onForward;
    $[119] = registerComposerFlush;
    $[120] = resumeDraft;
    $[121] = threadId;
    $[122] = threadScope;
    $[123] = t41;
  } else {
    t41 = $[123];
  }
  let t42;
  if ($[124] !== t19 || $[125] !== t38 || $[126] !== t39 || $[127] !== t40 || $[128] !== t41) {
    t42 = <div ref={itemRef} className={t19}>{t38}{t39}{t40}{t41}</div>;
    $[124] = t19;
    $[125] = t38;
    $[126] = t39;
    $[127] = t40;
    $[128] = t41;
    $[129] = t42;
  } else {
    t42 = $[129];
  }
  return t42;
}
function _temp4(a) {
  return a.isInline;
}